import { b as addItemByForm, a as contexts } from './cart-cce84c00.js';
import { E as EVENTS } from './events-58bc9098.js';
import { s as srraf } from './srraf.es-487187f3.js';

const __spd = (tag, ...args) => { try { if (localStorage.getItem('sp_cart_debug') === '1' || /[?&]debugCart=1/i.test(location.search)) { localStorage.setItem('sp_cart_debug','1'); console.log(`%c[SP/${tag}]%c +${(performance.now()/1000).toFixed(3)}s`, 'color:#4C6CF8;font-weight:bold', 'color:#888', ...args); } } catch {} };

/* The product's own name, for naming a bundle after what the customer actually bought.
 *
 * Every caller used to hunt for an h1, and the set templates render the title as a DIV —
 * `.product-details__title` — with NO h1 anywhere on the page. So the lookup came back
 * empty, the code fell through to CIKB's supplied label, and a leash-and-collar set
 * reached the checkout named "Charms — Bundle". Order matters below: the card and modal
 * titles inside the builder are also "titles" and must never win. */
const __productTitle = (scopeEl) => {
  const sec = (scopeEl && scopeEl.closest && scopeEl.closest('[js-product-section]'))
    || document.querySelector('[js-product-section]') || document;
  const sels = ['[js-product-title]', '.product-details__title',
                '.product-block--title h1', 'h1'];
  for (const sel of sels) {
    const el = sec.querySelector(sel);
    const txt = ((el && el.textContent) || '').trim().replace(/\s+/g, ' ');
    if (txt) return txt;
  }
  /* Last resort: the document title, minus the " - <shop>" Shopify appends. */
  try {
    const t = String(document.title || '').split(' - ')[0].trim();
    if (t) return t;
  } catch (e) {}
  return '';
};
const __spdKeys = (items) => (items || []).map(i => ({ key: i.key, variant: i.variant_id, title: i.title || i.product_title, props: Object.keys(i.properties || {}) }));

class ProductBlockBuyButtons extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      productContainer: "main-product-default, [js-quick-view-content]",
      detailsContainer: ".product__details",
      storeAvailabilityDrawerTrigger: "[js-store-availability-drawer-trigger]",
      variantSelectorContent: "product-block-variant-selector",

      // Sticky add to cart
      footer: "footer",
      buyButtons: "[js-buy-buttons]",
      stickyATC: "[js-sticky-atc]",
      stickyATCAddToCartButton: "[js-sticky-atc-add-to-cart-button-wrapper]",
      stickyATCDialog: "[data-fluco-dialog='sticky-add-to-cart-modal']",
      stickyATCModalHandlebar: "[js-sticky-atc-modal-handlebar]",
      stickyATCModalTrigger: "[js-sticky-atc-modal-trigger]",
      stickyATCModalClose: "[js-sticky-atc-modal-close]",
      stickyATCContent: "[js-sticky-atc-content]",
      stickyATCVariantContainer: "[js-sticky-atc-variant-container]",
      stickyATCVariantInput: "[js-variant-input]",
      stickyATCVariantButton: "[js-variant-button]",

      // Add to cart success and error
      addToCartButton: "[js-add-to-cart-button-wrapper]",
      addToCartError: "[js-add-to-cart-error]",
      giftCardRecipientForm: ".gift-card-recipient__fields",
      giftCardRecipientInput: ".gift-card-recipient__input",
      productForm: "[data-product-form]",
      showGiftCardRecipientCheckbox: "#gift-card-show-form",
    };

    this.classes = {
      hasError: "has-error",
      active: "active",
      changePending: "change-pending",
      heightCheckPending: "height-check-pending",
      delayRevealAnimation: "delay-reveal-animation",
    };

    this.strings = window.theme?.strings || {};
  }

  connectedCallback() {
    this.controller = new AbortController();
    const eventOptions = { signal: this.controller.signal };

    this.productContainer = this.closest(this.selectors.productContainer);
    this.detailsContainer = this.productContainer?.querySelector(
      this.selectors.detailsContainer,
    );

    // Listen for quantity change
    this.productContainer?.addEventListener(
      EVENTS.QUANTITY_INPUT_CHANGE,
      () => this.classList.remove(this.classes.hasError),
      eventOptions,
    );

    // Listen for variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      eventOptions,
    );

    this.initContentListeners();

    // Sticky add to cart content & listeners
    this.stickyATC = this.querySelector(this.selectors.stickyATC);
    if (this.stickyATC) {
      this.stickyATCContent = this.querySelector(this.selectors.stickyATCContent);
      this.stickyATCHasModalContent =
        this.stickyATC.getAttribute("data-has-modal-content") === "true";

      this.stickyATCAddToCartButtons = this.querySelectorAll(
        this.selectors.stickyATCAddToCartButton,
      );
      this.stickyATCDialog = this.querySelector(this.selectors.stickyATCDialog);

      this.initStickyATC();
    }
  }

  //
  // General setup functions
  //
  initContentListeners() {
    this.contentListenersController = new AbortController();
    const eventOptions = { signal: this.contentListenersController.signal };

    // Assign load and reassign on variant change
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);

    // Gift card recipient form
    this.enableGiftCardRecipientForm =
      this.getAttribute("data-enable-gift-card-recipient-form") === "true";

    this.showGiftCardRecipientCheckbox = this.querySelector(
      this.selectors.showGiftCardRecipientCheckbox,
    );
    if (this.enableGiftCardRecipientForm && this.showGiftCardRecipientCheckbox) {
      this.setupGiftCardRecipientForm();
    }

    // Store availability drawer
    this.storeAvailabilityDrawerTrigger = this.querySelector(
      this.selectors.storeAvailabilityDrawerTrigger,
    );
    if (this.storeAvailabilityDrawerTrigger) {
      this.drawerId =
        this.storeAvailabilityDrawerTrigger.getAttribute("data-drawer-id");

      this.storeAvailabilityDrawerTrigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent(EVENTS.MODAL_POPUP_OPEN(this.drawerId)),
          );
        },
        eventOptions,
      );
    }

    // Add to cart submit
    this.productForm = this.querySelector(this.selectors.productForm);
    this.productForm?.addEventListener(
      "submit",
      (event) => this.handleFormSubmission(event),
      eventOptions,
    );

    // Add to cart error
    this.addToCartError = this.querySelector(this.selectors.addToCartError);
    this.addEventListener(
      EVENTS.PRODUCT_ADD_ERROR,
      this.handleAddToCartError.bind(this),
      eventOptions,
    );

    // Success (theme’s default path)
    this.addEventListener(
      EVENTS.PRODUCT_ADD_SUCCESS,
      () => {
        this.classList.remove(this.classes.hasError);
        this.resetGiftCardRecipientForm();
        this.handleAddToCartSuccess();
      },
      eventOptions,
    );
  }

  //
  // Gift card functions
  //
  setupGiftCardRecipientForm() {
    this.giftCardRecipientForm = this.querySelector(
      this.selectors.giftCardRecipientForm,
    );
    this.giftCardRecipientInputs = this.querySelectorAll(
      this.selectors.giftCardRecipientInput,
    );

    this.showGiftCardRecipientCheckbox.checked = false;
    if (this.giftCardRecipientForm) this.giftCardRecipientForm.style.display = "none";

    this.showGiftCardRecipientCheckbox.addEventListener(
      "change",
      () => {
        if (!this.giftCardRecipientForm) return;
        if (this.showGiftCardRecipientCheckbox.checked) {
          this.giftCardRecipientForm.style.display = "block";
        } else {
          this.resetGiftCardRecipientForm();
        }
      },
      { signal: this.controller.signal },
    );
  }

  resetGiftCardRecipientForm() {
    if (!this.giftCardRecipientInputs || !this.giftCardRecipientForm) return;

    this.giftCardRecipientInputs.forEach((input) => {
      input.value = "";
      input.classList.remove("has-error");
    });

    if (this.showGiftCardRecipientCheckbox) {
      this.showGiftCardRecipientCheckbox.checked = false;
    }
    this.giftCardRecipientForm.style.display = "none";
  }

  //
  // Sticky Add to Cart functions
  //
  initStickyATC(options = {}) {
    if (this.stickyATCHasModalContent) {
      this.initStickyATCModalContent();
    } else {
      this.stickyATC.classList.remove(this.classes.delayRevealAnimation);
    }

    if (options.onVariantChange) {
      setTimeout(() => this.initStickyATCListeners(this.stickyATCHasModalContent), 0);
    } else {
      this.initStickyATCListeners(this.stickyATCHasModalContent);
    }
  }

  initStickyATCListeners(hasModalContent) {
    this.stickyATCListenersController = new AbortController();
    const eventOptions = { signal: this.stickyATCListenersController.signal };

    const observerOptions = { rootMargin: "0px", threshold: 0 };

    const observerCallback = (entries) => {
      let showStickyATC = null;
      for (const entry of entries) {
        showStickyATC = true;
        this.buyButtonsOrFooterIntersecting = entry.isIntersecting;

        if (this.buyButtonsOrFooterIntersecting) {
          if ((hasModalContent && !this.stickyATCDialog.open) || !hasModalContent) {
            showStickyATC = false;
            break;
          }
        }
      }
      this.updateStickyATCVisibility({ showStickyATC });
    };

    this.observer = new IntersectionObserver(observerCallback, observerOptions);
    this.buyButtonBlock = this.querySelector(this.selectors.buyButtons);
    if (this.buyButtonBlock) this.observer.observe(this.buyButtonBlock);
    this.footer = document.querySelector(this.selectors.footer);
    if (this.footer) this.observer.observe(this.footer);

    if (hasModalContent) {
      // Click to open modal
      this.stickyATCModalTrigger = this.stickyATCContent?.querySelector(
        this.selectors.stickyATCModalTrigger,
      );
      this.stickyATCModalTrigger?.addEventListener(
        "click",
        this.openStickyATCModal,
        eventOptions,
      );

      // Handlebar gestures
      this.peekingStickyATCHandlebar = this.stickyATCContent?.querySelector(
        this.selectors.stickyATCModalHandlebar,
      );
      this.activeStickyATCHandlebar = this.stickyATCDialog?.querySelector(
        this.selectors.stickyATCModalHandlebar,
      );

      this.peekingStickyATCHandlebar?.addEventListener(
        "pointerdown",
        (event) => this.touchSlideStickyATCModal(event, "open"),
        eventOptions,
      );

      this.activeStickyATCHandlebar?.addEventListener(
        "pointerdown",
        (event) => this.touchSlideStickyATCModal(event, "close"),
        eventOptions,
      );

      // Resize watcher
      this.widthWatcher = srraf(({ vw, pvw }) => {
        if (vw === pvw) return;
        this.stickyATCDialog.classList.add(this.classes.heightCheckPending);
        this.setStickyATCAnimationHeight();
      });

      // Modal closed
      window.addEventListener(
        EVENTS.MODAL_POPUP_CLOSED("sticky-add-to-cart-modal"),
        () => {
          if (this.buyButtonsOrFooterIntersecting) {
            this.updateStickyATCVisibility({ showStickyATC: false });
          }
          this.stickyATCDialog.classList.add("modal--will-open");
        },
        eventOptions,
      );

      // Close via link
      this.stickyATCModalCloseLink = this.stickyATCDialog?.querySelector(
        this.selectors.stickyATCModalClose,
      );

      this.stickyATCModalCloseLink?.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.closeStickyATCModal();
        },
        eventOptions,
      );

      // Cart drawer opening closes modal
      window.addEventListener(
        EVENTS.CART_DRAWER_OPENING,
        () => { if (this.stickyATCDialog?.open) this.closeStickyATCModal(); },
        eventOptions,
      );

      // Variant clicks in modal
      this.stickyATCVariantInputs = this.querySelectorAll(
        this.selectors.stickyATCVariantInput,
      );
      this.stickyATCVariantInputs.forEach((input) => {
        input.addEventListener("change", this.onStickyATCVariantChange, eventOptions);
      });

      this.stickyATCVariantButtons = this.querySelectorAll(
        this.selectors.stickyATCVariantButton,
      );
      this.stickyATCVariantButtons.forEach((button) => {
        button.addEventListener(
          "keydown",
          (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              const swatchButton = button.getAttribute("js-swatch");
              const chipButton = button.getAttribute("js-chip");
              if (swatchButton !== null) {
                const swatchWrapper = event.target.parentElement;
                const inputHtml = swatchWrapper.previousElementSibling;
                this.onStickyATCVariantChangeA11y(inputHtml);
              } else if (chipButton !== null) {
                this.onStickyATCVariantChangeA11y(
                  event.target.previousElementSibling,
                );
              }
            }
          },
          eventOptions,
        );
      });
    }
  }

  touchSlideStickyATCModal(event, state) {
    this.setPointerCapture?.(event.pointerId);
    this.onpointerup = () => {
      if (state === "open") this.openStickyATCModal(event);
      else if (state === "close") this.closeStickyATCModal(event);
    };
  }

  openStickyATCModal = (event) => {
    event?.preventDefault?.();
    window.dispatchEvent(
      new CustomEvent(EVENTS.MODAL_POPUP_OPEN("sticky-add-to-cart-modal")),
    );
  };

  closeStickyATCModal() {
    window.dispatchEvent(
      new CustomEvent(EVENTS.MODAL_POPUP_CLOSE("sticky-add-to-cart-modal")),
    );
  }

  updateStickyATCVisibility(options = {}) {
    if (options.showStickyATC) {
      this.stickyATC.classList.add(this.classes.active);
      this.visibleStickyATC = true;
    } else {
      this.stickyATC.classList.remove(this.classes.active);
      this.visibleStickyATC = false;
    }
  }

  initStickyATCModalContent() {
    this.variantSelectorContent = document.querySelector(
      this.selectors.variantSelectorContent,
    );
    this.stickyATCVariantContainer = this.stickyATCDialog?.querySelector(
      this.selectors.stickyATCVariantContainer,
    );

    if (this.stickyATCVariantContainer && this.variantSelectorContent) {
      this.stickyATCVariantContainer.innerHTML =
        this.variantSelectorContent.innerHTML;
    }

    this.setStickyATCAnimationHeight();
  }

  setStickyATCAnimationHeight() {
    this.stickyATCContent = this.querySelector(this.selectors.stickyATCContent);
    if (!this.stickyATCContent || !this.stickyATCDialog) return;

    this.peekingHeight = this.stickyATCContent.getBoundingClientRect().height;

    const availableHeight = window.innerHeight * 0.9; // 90svh
    const dialogHeight = this.stickyATCDialog.getBoundingClientRect().height;
    const modalRevealHeight = (
      Math.min(availableHeight, dialogHeight) - this.peekingHeight
    ).toFixed(2);

    this.stickyATCDialog.style.setProperty(
      "--modal-reveal-height",
      `${modalRevealHeight}px`,
    );
    this.stickyATCDialog.classList.remove(this.classes.heightCheckPending);
    this.stickyATC.classList.remove(this.classes.delayRevealAnimation);
  }

  onStickyATCVariantChange = (event) => {
    this.variantLayout = this.getAttribute("data-variant-layout");

    let optionValueElement;
    if (this.variantLayout === "swatches" || this.variantLayout === "chips") {
      optionValueElement = event.target;
    } else if (this.variantLayout === "dropdown") {
      optionValueElement = event.target.selectedOptions[0];
    }

    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: { newOptionInput: optionValueElement, withinStickyModal: true },
      },
    );

    this.dispatchEvent(variantChangingEvent);
  };

  onStickyATCVariantChangeA11y(inputHtml) {
    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: { newOptionInput: inputHtml, withinStickyModal: true },
      },
    );
    this.dispatchEvent(variantChangingEvent);
  }

  //
  // Variant and quantity change handling functions
  //
  handleRepaint() {
    this.contentListenersController?.abort();
    this.initContentListeners();

    if (this.stickyATC) {
      this.stickyATCAddToCartButtons = this.querySelectorAll(
        this.selectors.stickyATCAddToCartButton,
      );
      this.stickyATCDialog = this.querySelector(this.selectors.stickyATCDialog);

      this.stickyATCListenersController?.abort();
      this.initStickyATC({ onVariantChange: true });
    }
  }

  //
  // Add to cart and results handling
  //
  handleFormSubmission(event) {
    event.preventDefault();

    // Animations
    this.detailsContainer?.classList.add(this.classes.changePending);
    this.addToCartButton?.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_STARTED));
    if (this.stickyATCAddToCartButtons && this.visibleStickyATC) {
      this.stickyATCAddToCartButtons.forEach((button) =>
        button.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_STARTED)),
      );
    }

    // Peek: do we have *any* staged items (charms or MPB)?
    const hasStaged =
      this.collectExternalItems(true /* peek */).length > 0;

    // If nothing staged → normal theme flow
    if (!hasStaged) {
      addItemByForm(this.productForm, contexts.productPage, this);
      return;
    }

    // Otherwise, single combined request + immediate drawer open with hydrated sections
    this.addCombinedMainPlusStaged()
      .then(() => this.handleAddToCartSuccess())
      .catch((e) =>
        this.handleAddToCartError({
          detail: { errorMessage: { description: e?.message || 'Add to cart failed' } },
        })
      );
  }

  handleAddToCartSuccess() {
    this.detailsContainer?.classList.remove(this.classes.changePending);
    this.addToCartButton?.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED));

    this.productContainer?.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_ADD_SUCCESS),
    );

    if (this.stickyATCAddToCartButtons) {
      this.stickyATCAddToCartButtons.forEach((button) => {
        button.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED));
      });
    }
  }

  handleAddToCartError(event) {
    this.detailsContainer?.classList.remove(this.classes.changePending);
    this.addToCartButton?.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED));

    if (this.stickyATCAddToCartButtons) {
      this.stickyATCAddToCartButtons.forEach((button) => {
        button.dispatchEvent(new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED));
      });

      if (this.stickyATCHasVariants && this.stickyATCDialog?.open) {
        this.closeStickyATCModal();
      }
    }

    const formErrors = event?.detail?.errorMessage?.errors;
    this.classList.add(this.classes.hasError);

    if (formErrors) {
      const sectionId = this.querySelector(this.selectors.giftCardRecipientForm)?.getAttribute("data-section-id");
      if (this.addToCartError) this.addToCartError.innerText = "";

      Object.entries(formErrors).forEach(([key]) => {
        const errorMessageID = `gift-card-recipient-${key}-error--${sectionId}`;
        const errorInput = this.querySelector(`#gift-card-recipient-${key}--${sectionId}`);
        errorInput?.classList.add(this.classes.hasError);
        errorInput?.setAttribute("aria-invalid", true);
        errorInput?.setAttribute("aria-describedby", errorMessageID);

        if (this.addToCartError) {
          const msg = this.strings?.products?.giftCardRecipientErrors?.[key] || key;
          this.addToCartError.innerText += `${msg}\n`;
        }
      });
    } else if (this.addToCartError) {
      this.addToCartError.innerText = event?.detail?.errorMessage?.description || 'Error';
    }

    const inQuickView = Boolean(
      this.closest("[data-section-type='quick-view-product']"),
    );
    if (!inQuickView) this.addToCartError?.scrollIntoView({ block: "center" });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.contentListenersController?.abort();
    this.stickyATCListenersController?.abort();
  }

  //
  // Helpers
  //

  // Combine staged items from both Charms (CIKB) and Multi-Product Bundle (MPB)
  collectExternalItems(peek = false) {
    // Charms: peek when peeking; pop when not
    const fromCharms = peek
      ? (window.CIKB?.peekLineItems?.() || [])
      : (window.CIKB?.getStagedLineItems?.(true) || []);

    // MPB: API is getStagedLineItems(peek=false) → clears when peek===false
    const fromMPB = window.MPB?.getStagedLineItems?.(peek === true) || [];

    // MQB (quantity tiers, "Buy 2 Get 1 Free"): same contract as MPB.
    // Unit 1 is NOT staged — MQB writes it into the form's input[name="id"] so it
    // becomes the main line item / bundle parent.
    const fromMQB = window.MQB?.getStagedLineItems?.(peek === true) || [];

    return [...fromCharms, ...fromMPB, ...fromMQB];
  }


  // Serialize the product form into ONE Shopify line item for /cart/add.js JSON
  serializeMainLineItem() {
    const fd = new FormData(this.productForm);

    // Required
    const id = fd.get('id');
    const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;

    // Optional fields
    const selling_plan = fd.get('selling_plan') || undefined;

    // properties[Key] → properties object
    const properties = {};
    for (const [k, v] of fd.entries()) {
      const m = k.match(/^properties\[(.+?)\]$/);
      if (m && m[1] != null) properties[m[1]] = v;
    }

    const item = { id: Number(id), quantity };
    if (selling_plan) item.selling_plan = selling_plan;
    if (Object.keys(properties).length) item.properties = properties;

    return item;
  }

  async addCombinedMainPlusStaged() {
    __spd('atc.start', 'addCombinedMainPlusStaged()');

    // 1) Main item (current product) — this will be the bundle parent in your transform
    const mainItem = this.serializeMainLineItem();
    __spd('atc.mainItem', mainItem);

    // 2) Pull + clear all staged (charms + MPB)
    /* Snapshot the staged BUNDLES before popping them.
     *
     * The post-add restore used to read a snapshot kept by charm_bar, but that snapshot is
     * refreshed on every staged:change — and the pop below IS a change, so by the time
     * sp:staged:consumed fired the remembered value was already empty and the customer's
     * configuration vanished from the PDP. The add knows exactly what it consumed, so it
     * carries it on the event instead of leaving the listener to guess. */
    const consumedBundles = (() => {
      try {
        return (window.CIKB?.getStagedBundles?.() || []).map((b) => ({
          ...b,
          items: (b.items || []).map((it) => ({ ...it })),
          display: (b.display || []).map((d) => ({ ...d })),
          meta: { ...(b.meta || {}) },
        }));
      } catch (e) { return []; }
    })();

    let stagedItems = this.collectExternalItems(false); // pops from both CIKB + MPB

    /* Snapshot how the charms divide into SETS before anything re-homes them.
     *
     * The grouping below rewrites every charm's bundleId to a single parent bundle, so
     * counting sets afterwards always found exactly one — and the cart drew two sets of
     * charms as one long strip. CIKB gives each staged set its own bundleId; that is the
     * only place the shape still exists. */
    /* Letters in SET order, captured here for the same reason as the counts: the grouping
     * below rewrites every charm's bundleId to one parent bundle, so by the time the
     * sequence is built the set boundaries are gone and a global _OrderIndex sort
     * interleaves them. */
    const charmSetOrder = [];
    const charmSetShape = (() => {
      const seen = new Map();
      const counts = [];
      /* CHARMS only. In mirror mode the $0 mirror units are staged alongside the charms,
       * and counting them produced a phantom third set — "2,3,2" for two sets of 2 and 3 —
       * which the cart then tried to draw as a third strap. A charm is the thing with a
       * _Letter on it; nothing else belongs in this shape. */
      const groups = [];
      stagedItems.forEach((it) => {
        if (!(it && it.properties && it.properties._Letter)) return;
        const key = it.properties.bundleId || '__loose';
        if (!seen.has(key)) { seen.set(key, counts.length); counts.push(0); groups.push([]); }
        counts[seen.get(key)] += Number(it.quantity) || 1;
        groups[seen.get(key)].push(it);
      });
      groups.forEach((items) => {
        items
          .slice()
          .sort((a, b) => Number(a.properties._OrderIndex) - Number(b.properties._OrderIndex))
          .forEach((it) => charmSetOrder.push(String(it.properties._Letter)));
      });
      return counts;
    })();

    /* Offer blocks contribute extra lines that belong in this product's bundle --
     * "add a leash and your charms are free" queues the matching leash here. They
     * carry no bundleId, so the grouping below treats them exactly like MPB children
     * and re-homes them under this product. */
    try {
      const offerItems = (window.__spOfferItems || []).filter(Boolean);
      if (offerItems.length) {
        stagedItems = stagedItems.concat(offerItems.map((it) => ({
          ...it, properties: { ...(it.properties || {}) }
        })));
      }
    } catch (e) {}

    /* A stage-only bundle block owns the whole bundle. Its parent product supplies the
     * bundleParentVariantId below (display identity for linesMerge) but must NOT be
     * added as a line item — otherwise the cart shows the merged bundle AND a redundant
     * parent line, and deleting that line orphans the children. */
    const stageOnlyBundle = stagedItems.some((it) => it?.properties?._MPBStageOnly === '1');
    __spd('atc.stagedItems', { count: stagedItems.length, items: stagedItems });
    if (!stagedItems.length) {
      // Race: nothing left staged → fall back to normal theme path
      __spd('atc.stagedItems.empty → fallback to addItemByForm');
      addItemByForm(this.productForm, contexts.productPage, this);
      return;
    }

    // Quantity-bundle lines running in 'auto' mode are priced by a native Shopify
    // "Buy X get Y" automatic discount, which needs to see the individual qualifying
    // lines. Merging them into a bundle group would hide those lines from the
    // discount engine, so they pass through untouched — no bundleId, no re-homing.
    const isStandalone = (it) => it?.properties?._MQBStandalone === '1';

    // 3) Detect which staged items are MPB (they come without bundleId)
    const isMPBChild = (it) => !isStandalone(it) && !(it?.properties && 'bundleId' in it.properties);

    // We’ll use one bundleId/title for the MPB group under this parent
    const toGid = (id) =>
      (String(id).startsWith('gid://') ? String(id) : `gid://shopify/ProductVariant/${id}`);
    const parentGid = toGid(mainItem.id);
    const mpbBundleId = (crypto?.randomUUID?.() || `mpb-${Date.now()}`);
    /* Prefer a title the bundle block supplied (the actual product name); fall back to
     * the generic label only when none was provided. */
    const suppliedTitle = stagedItems
      .map((it) => it?.properties?.bundleTitle)
      .find((t) => typeof t === 'string' && t.trim());
    // 4) If there are MPB children, tag the PARENT too so the transform doesn’t duplicate it
    const hasMPBChildren = stagedItems.some(isMPBChild);

    /* Charms staged from a product PDP belong to THAT product's bundle. The customer
     * personalised one collar; they did not buy a collar and, separately, a loose set
     * of charms — which is what the cart showed, two lines and a basket count of 2.
     * Only a stage-only add (no parent line is created at all) leaves each charm in
     * the bundle CIKB gave it. */
    /* A quantity bundle in MIRROR mode: attach each charm set to the leash it belongs to.
     *
     * In 'auto' mode the units must stay ungrouped or the BXGY discount stops seeing them,
     * so every charm set ended up under the single main line — nine charms on the Oat leash
     * and none on the other two. With $0 mirrors there is no discount to protect, so each
     * unit variant can carry its own bundle and its own charms. Sets on the same colour
     * share that colour's bundle, which is what the customer means by "these go on that one".
     */
    let perUnitCharms = false;
    try {
      const unitIds = (window.MQB && typeof window.MQB.getUnitVariantIds === 'function')
        ? window.MQB.getUnitVariantIds() : [];
      const mirrorMode = !!document.querySelector('.mqb[data-bid][data-discount-mode="mirror"]');
      if (mirrorMode && unitIds.length > 1) {
        // Charm sets, in the order they were staged, keyed by the bundleId CIKB gave them.
        const order = [];
        const seen = new Map();
        stagedItems.forEach((it) => {
          const b = it && it.properties && it.properties.bundleId;
          if (!b) return;
          if (!seen.has(b)) { seen.set(b, order.length); order.push([]); }
          order[seen.get(b)].push(it);
        });
        if (order.length) {
          perUnitCharms = true;
          const bundleForVariant = new Map();
          /* Read the product title here rather than using `ownTitle`, which is declared
           * further down: referencing it from this block is a temporal-dead-zone
           * ReferenceError that the try/catch would swallow, quietly turning the whole
           * feature off with nothing to show for it. */
          const titleFor = __productTitle(this) || suppliedTitle || 'Bundle';
          const idFor = (vid) => {
            if (!bundleForVariant.has(vid)) {
              bundleForVariant.set(vid, crypto?.randomUUID?.() || ('mqb-' + vid + '-' + Date.now()));
            }
            return bundleForVariant.get(vid);
          };
          order.forEach((set, i) => {
            const vid = String(unitIds[i % unitIds.length]);
            const bId = idFor(vid);
            set.forEach((it) => {
              it.properties = { ...(it.properties || {}), bundleId: bId,
                                bundleParentVariantId: toGid(vid), bundleTitle: titleFor };
            });
          });
          /* Stamp the unit lines with the same bundle so the transform pairs them up. Only
           * the variants that actually carry charms are touched — a leash nobody
           * personalised stays a plain line. */
          const stamp = (item) => {
            if (!item) return;
            const bId = bundleForVariant.get(String(item.id));
            if (!bId) return;
            item.properties = { ...(item.properties || {}), bundleId: bId,
                                bundleParentVariantId: toGid(item.id), bundleTitle: titleFor };
            delete item.properties._MQBStandalone;
          };
          stamp(mainItem);
          stagedItems.forEach(stamp);
        }
      }
    } catch (e) { __spd('atc.perUnitCharms failed', String(e)); }

    /* A mirror-mode volume tier is ONE bundle, even with no charms staged.
     *
     * MQB pairs a unit with its $0 mirror and leaves the other PAID units standalone, so
     * "Buy 2, Get 1 Free" on the bottles arrived as two cart lines: a bundle of one bottle
     * plus its free twin, and a second bottle loose beside it. The money was right and the
     * grouping was not, which is the worst combination — nothing looks broken enough to
     * chase. Only mirror mode is unified: 'auto' deliberately keeps its units ungrouped so
     * the BXGY discount can still see them.
     */
    if (!perUnitCharms) {
      try {
        const mirrorMode = !!document.querySelector('.mqb[data-bid][data-discount-mode="mirror"]');
        /* Everything being added from this PDP in mirror mode is one bundle, so do not
         * try to pick the units out by flag. MQB hands over only the FREE unit (marked
         * _FreeGift); the extra PAID units arrive by another path carrying no marker at
         * all, which is why a filter on _MQBStandalone or bundleId missed them and left a
         * bottle loose beside its own bundle. The charms path is excluded above. */
        const mqbItems = mainItem ? [mainItem, ...stagedItems] : stagedItems.slice();
        if (mirrorMode && mqbItems.length > 1) {
          const seeded = mqbItems.find((it) => it && it.properties && it.properties.bundleId);
          const bId = (seeded && seeded.properties.bundleId)
            || (crypto?.randomUUID?.() || 'mqb-' + Date.now() + '-' + Math.random());
          const parentVid = (seeded && seeded.properties.bundleParentVariantId)
            || toGid(mqbItems[0].id);
          const unifiedTitle = __productTitle(this) || suppliedTitle || 'Bundle';
          mqbItems.forEach((it) => {
            it.properties = {
              ...(it.properties || {}),
              bundleId: bId,
              bundleParentVariantId: parentVid,
              bundleTitle: unifiedTitle,
            };
            delete it.properties._MQBStandalone;
          });
        }
      } catch (e) { __spd('atc.unifyMirrorUnits failed', String(e)); }
    }

    /* Name the genuinely free lines here too, not only on the charms path.
     *
     * This lived inside the charm-sequence branch, so a volume bundle bought WITHOUT charms
     * carried no _FreeUnits at all and the cart had no way to tell the free unit from a
     * paid one. */
    try {
      const mqbRoot = document.querySelector('.mqb[data-bid]');
      const payloadNode = mqbRoot
        && document.getElementById('mqb-payload-' + mqbRoot.dataset.bid);
      if (payloadNode) {
        const pd = JSON.parse(payloadNode.textContent || 'null') || {};
        const mirrorIds = new Set(
          Object.keys(pd.mirror || {}).map((k) => String((pd.mirror[k] || {}).id)));
        const tally = new Map();
        stagedItems.forEach((it) => {
          const id = String(it && it.id);
          if (!mirrorIds.has(id)) return;
          tally.set(id, (tally.get(id) || 0) + (Number(it.quantity) || 1));
        });
        if (tally.size) {
          const val = Array.from(tally.entries()).map(([id, q]) => id + ':' + q).join(',');
          stagedItems = stagedItems.map((it) => ({
            ...it, properties: { ...(it.properties || {}), _FreeUnits: val } }));
          if (mainItem) {
            mainItem.properties = { ...(mainItem.properties || {}), _FreeUnits: val };
          }
        }
      }
    } catch (e) { __spd('atc.freeUnits(base) failed', String(e)); }

    /* ...but a quantity bundle in 'auto' mode has nothing to adopt. Every line it stages
     * is standalone, and the PAID units ride on the main line as QUANTITY. Giving that
     * main line a bundleId made the transform merge it into a solo bundle of quantity 1,
     * which hid the qualifying units from the discount engine: a "Buy 3, Get 2" cart of
     * 5 looked like 3 units, so only the Buy-2-Get-1 automatic discount could ever
     * match. Leave the main line ungrouped when the only staged items are MQB's. */
    const allStandalone = stagedItems.length > 0 && stagedItems.every(isStandalone);
    const adoptStagedCharms = !stageOnlyBundle && !!mainItem && !allStandalone && !perUnitCharms;
    const groupUnderParent = hasMPBChildren || adoptStagedCharms;

    /* When the set is a product plus its own charms, the parent line IS the product,
     * so the set is named after it -- CIKB's own "Charms" label, and the generic
     * "Bundle — MPB" fallback, both read as a different thing in the cart. */
    const ownTitle = __productTitle(this);
    /* Name the bundle after the PRODUCT, always.
     *
     * This preferred the product title only when the parent was adopting staged charms;
     * every other route fell back to whatever was supplied, which is CIKB's own
     * "Charms — Bundle". So a leash-and-collar set reached the checkout calling itself
     * "Charms — Bundle" — the charms are a component of that set, not its name, and the
     * customer is left reading a line item that does not match what they bought. */
    const mpbBundleTitle = ownTitle || suppliedTitle || 'Bundle — MPB';

    if (groupUnderParent) {
      mainItem.properties = {
        ...(mainItem.properties || {}),
        bundleId: mpbBundleId,
        bundleTitle: mpbBundleTitle
      };
      /* The parent has to name itself as the parent too. With only bundleId on this
       * line the transform grouped the charms (which carry both properties) and left
       * the collar beside them -- two lines, basket count 2. */
      if (adoptStagedCharms) mainItem.properties.bundleParentVariantId = parentGid;
    }

    // 5) Nest everything under THIS parent as ONE bundle.
    //    When MPB children are present (a leash + collar + charms "set"), unify
    //    the CHARMS into the SAME bundleId + parent as the MPB products so the
    //    cart-transform Function merges them into a single, unbreakable line —
    //    a customer can't remove the charms (or any one item) on their own, and
    //    the fixed price holds. When there is NO MPB group (a standalone charm
    //    PDP), leave each charm's own bundleId/parent/title untouched.
    stagedItems = stagedItems.map(it => {
      // Native-discount quantity bundle: never group these.
      if (isStandalone(it)) return it;
      // Nothing to adopt them into: preserve the charm's own bundle.
      if (!groupUnderParent && !isMPBChild(it)) return it;
      // Bundle set: re-home MPB children AND charms under the single parent,
      // preserving any charm-specific props (_Letter / _OrderIndex / _IsPreorder).
      return {
        ...it,
        properties: {
          ...(it.properties || {}),
          bundleId: mpbBundleId,
          bundleParentVariantId: parentGid,
          bundleTitle: mpbBundleTitle
        }
      };
    });

    /* 5b) Record the charm order on the PARENT line.
     *
     * The cart's "Set items" list is hydrated from _Details, whose order is decided
     * by the bundling app — it comes back alphabetical, so "RISHI" rendered as
     * H I I R S. The child charm lines carry _OrderIndex but get merged away before
     * /cart.js can be read, so the only durable place for the sequence is here. */
    /* Group by SET first, then order within the set.
     *
     * _OrderIndex restarts at 0 for every staged set, so sorting all charms by it globally
     * interleaves them: two sets A,B and C,D,E came out "A,C,B,D,E". The cart splits this
     * sequence using _CharmSets, so an interleaved order puts the wrong letters in each
     * strap — the counts look right and the straps read as nonsense. */
    const charmSequence = charmSetOrder.length ? charmSetOrder.slice() : (() => {
      const bySet = new Map();
      stagedItems.forEach((it) => {
        if (!(it && it.properties && it.properties._Letter)) return;
        const key = it.properties.bundleId || '__loose';
        if (!bySet.has(key)) bySet.set(key, []);
        bySet.get(key).push(it);
      });
      const out = [];
      bySet.forEach((items) => {
        items
          .slice()
          .sort((a, b) => Number(a.properties._OrderIndex) - Number(b.properties._OrderIndex))
          .forEach((it) => out.push(String(it.properties._Letter)));
      });
      return out;
    })();
    /* The set's value anchor, computed on the PDP where the entitlement is known.
     * The cart can't work it out from line items alone (it can't know the set allows
     * 8 free charms), so carry it. */
    let setValueCents = 0;
    try {
      const cmp = document.querySelector('.mpb__headline-was');
      if (cmp && !cmp.hidden) {
        const digits = String(cmp.textContent || '').replace(/[^0-9.]/g, '');
        if (digits) setValueCents = Math.round(parseFloat(digits) * 100);
      }
    } catch (e) {}

    /* Resolve the collar's colour for the CART.
     *
     * This used to read storage only, and only inside the charm-sequence branch — so a
     * set with charms but no _Letter, or a page where nothing had written the keys, got
     * no strap colour at all and the cart strip rendered bare. Read the live selection as
     * a fallback so the colour does not depend on another script having run first, and
     * attach it regardless of whether a letter sequence was built.
     *
     * Ask the COLLAR's own swatch: with independent colours the first colour row on the
     * page is the leash, and the strip is the collar's webbing. */
    function __strapFromDom() {
      const sw =
        document.querySelector('.mpb-card[data-is-collar="true"] .mpb-swatch.is-active') ||
        document.querySelector('.mpb-shared-color .mpb-swatch.is-active') ||
        document.querySelector('.mpb-opt--color .mpb-swatch.is-active');
      if (!sw) return { hex: '', img: '' };
      const label = (sw.getAttribute('aria-label') || '').trim().toLowerCase();
      let img = '';
      const raw = (document.querySelector('[data-strap-colors]') || {}).dataset?.strapColors || '';
      raw.split(/[\r\n]+/).forEach((line) => {
        const i = line.indexOf('|');
        if (i < 0) return;
        if (line.slice(0, i).trim().toLowerCase() === label) {
          const v = line.slice(i + 1).trim();
          if (v && !/^(#|rgb|hsl)/i.test(v)) img = v;
        }
      });
      const fill = sw.querySelector('.mpb-swatch__fill') || sw;
      const bg = getComputedStyle(fill).backgroundColor;
      return { hex: bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '', img };
    }

    {
      let sHex = '', sImg = '';
      try { sHex = sessionStorage.getItem('mpbStrapHex') || ''; } catch (e) {}
      try { sImg = sessionStorage.getItem('mpbStrapImg') || ''; } catch (e) {}
      if (!sHex || !sImg) {
        const dom = __strapFromDom();
        if (!sHex) sHex = dom.hex;
        if (!sImg) sImg = dom.img;
      }
      if (sHex || sImg) {
        const strapExtra = {};
        if (sHex) strapExtra._StrapHex = sHex;
        if (sImg) strapExtra._StrapImg = sImg;
        stagedItems = stagedItems.map((it) => ({
          ...it,
          properties: { ...(it.properties || {}), ...strapExtra }
        }));
        if (!stageOnlyBundle && mainItem) {
          mainItem.properties = { ...(mainItem.properties || {}), ...strapExtra };
        }
        try { if (sHex) localStorage.setItem('mpbStrapHex', sHex); } catch (e) {}
        try {
          if (sImg) localStorage.setItem('mpbStrapImg', sImg);
          else localStorage.removeItem('mpbStrapImg');
        } catch (e) {}
      }
    }

    if (charmSequence.length) {
      /* Stamp EVERY line, not just one. The line that ends up carrying _Details (and
       * therefore rendering the "Set items" list) is the merged parent the bundling
       * app creates — we don't know which of ours becomes it, and under stage_only
       * there's no mainItem at all. Putting the sequence on all of them means the
       * cart can always find it. */
      const seqValue = charmSequence.join(',');
      let strapHex = '';
      try { strapHex = sessionStorage.getItem('mpbStrapHex') || ''; } catch (e) {}
      let strapImg = '';
      try { strapImg = sessionStorage.getItem('mpbStrapImg') || ''; } catch (e) {}
      /* Belt and braces: the bundling app rewrites line properties on the merged
       * parent, so the sequence may not survive on any line we control. Keep a copy
       * client-side that the cart can fall back to. */
      /* localStorage as well as sessionStorage: once the bundling app merges the
       * lines it keeps only _Details, so on a later visit the cart has no record of
       * the sequence or the strap colour at all. */
      try { sessionStorage.setItem('mpbCharmOrder', seqValue); } catch (e) {}
      try { localStorage.setItem('mpbCharmOrder', seqValue); } catch (e) {}
      if (strapHex) { try { localStorage.setItem('mpbStrapHex', strapHex); } catch (e) {} }
      try {
        if (strapImg) localStorage.setItem('mpbStrapImg', strapImg);
        else localStorage.removeItem('mpbStrapImg');
      } catch (e) {}
      /* How the charms divide into SETS, and the colour each set wears.
       *
       * The cart only receives `_Details` — variant ids and quantities — so per-item
       * properties never reach it and every charm collapsed into one strip: two sets on one
       * collar came out as a single seven-charm row in one colour. Send the shape of the
       * grouping on the line itself. Counts are in the same order as _CharmOrder. */
    /* The colour each charm SET wears, in bundle order.
     *
     * The rule, which is about what the customer is actually building:
     *   - a VOLUME bundle: set N wears the colour of volume item N in the selected tier,
     *     cycling once the units run out (Buy 3 in Lilac/Green/Oat -> Lilac, Green, Oat, Lilac...)
     *   - an MPB set: the collar first, then the charm strap, then the leash, then repeat
     *   - a plain product PDP: that product's own colour, repeated
     *
     * Before this every set was stamped with the PARENT's colour, so two sets on a
     * three-leash volume bundle came out as one lilac strip regardless of what was in it.
     */
    function __bundleUnitColors() {
      // Volume bundle: the selected units, in the order they were chosen.
      try {
        const root = document.querySelector('.mqb[data-bid]');
        const ids = (window.MQB && typeof window.MQB.getUnitVariantIds === 'function')
          ? window.MQB.getUnitVariantIds().map(String) : [];
        const node = root && document.getElementById('mqb-payload-' + root.dataset.bid);
        if (ids.length && node) {
          const d = JSON.parse(node.textContent || 'null') || {};
          const sw = d.colorSwatches || {};
          const byId = new Map((d.variants || []).map((v) => [String(v.id), v]));
          const out = ids.map((id) => {
            const v = byId.get(id);
            if (!v) return '';
            const name = (v.options || []).find((x) => sw[x]) || (v.options || [])[0];
            const st = sw[name] || {};
            return st.image || st.color || '';
          });
          if (out.some(Boolean)) return out;
        }
      } catch (e) { __spd('atc.unitColors mqb failed', String(e)); }

      // MPB set: collar, then charm strap, then leash.
      try {
        const cards = Array.prototype.slice.call(document.querySelectorAll('.mpb-card'));
        const rank = (c) => {
          if (c.dataset && c.dataset.isCollar === 'true') return 0;
          return /strap/i.test(c.textContent || '') ? 1 : 2;
        };
        const out = cards
          .slice()
          .sort((a, b) => rank(a) - rank(b))
          .map((c) => {
            const el = c.querySelector('.mpb-swatch.is-active .mpb-swatch__fill')
              || c.querySelector('.mpb-swatch.is-active');
            if (!el) return '';
            const bg = getComputedStyle(el).backgroundColor;
            return bg && bg !== 'rgba(0, 0, 0, 0)' ? bg : '';
          })
          .filter(Boolean);
        if (out.length) return out;
      } catch (e) { __spd('atc.unitColors mpb failed', String(e)); }

      return [];
    }

      const setCounts = charmSetShape.slice();
      const setStraps = [];
      const unitColors = __bundleUnitColors();
      try {
        for (let i = 0; i < setCounts.length; i++) {
          // Bundle order first, cycling; then the set's own strap; then the parent colour.
          let c = unitColors.length ? (unitColors[i % unitColors.length] || '') : '';
          if (!c) {
            const st = (window.CIKB && window.CIKB.strapStyleForSet)
              ? window.CIKB.strapStyleForSet(i) : null;
            c = (st && (st.image || st.color)) || '';
          }
          setStraps.push(c);
        }
        if (!setStraps.some(Boolean) && strapHex) {
          for (let i = 0; i < setStraps.length; i++) setStraps[i] = strapHex;
        }
      } catch (e) { __spd('atc.charmSets failed', String(e)); }

      /* Which lines are GENUINELY free.
       *
       * The free units of a volume tier are $0 mirror variants. The cart cannot see per-line
       * properties — only _Details — so it has no way to tell a free unit from a paid one,
       * and the hydrator's price redistribution gave them a zero share and dropped them from
       * the breakdown entirely: a "Buy 2, Get 1 Free" cart listed two leashes, not three.
       * Name them on the line so the cart can render them as FREE instead of losing them. */
      let freeUnitsValue = '';
      try {
        const root = document.querySelector('.mqb[data-bid]');
        const node = root && document.getElementById('mqb-payload-' + root.dataset.bid);
        if (node) {
          const d = JSON.parse(node.textContent || 'null') || {};
          const mirrorIds = new Set(
            Object.keys(d.mirror || {}).map((k) => String((d.mirror[k] || {}).id)));
          const tally = new Map();
          stagedItems.forEach((it) => {
            const id = String(it && it.id);
            if (!mirrorIds.has(id)) return;
            tally.set(id, (tally.get(id) || 0) + (Number(it.quantity) || 1));
          });
          freeUnitsValue = Array.from(tally.entries()).map(([id, q]) => id + ':' + q).join(',');
        }
      } catch (e) { __spd('atc.freeUnits failed', String(e)); }

      const extra = { _CharmOrder: seqValue };
      if (freeUnitsValue) extra._FreeUnits = freeUnitsValue;
      if (setCounts.length > 1) {
        extra._CharmSets = setCounts.join(',');
        extra._CharmSetStrap = setStraps.join(',');
      }
      if (strapHex) extra._StrapHex = strapHex;
      if (strapImg) extra._StrapImg = strapImg;
      if (setValueCents > 0) extra._SetValue = String(setValueCents);
      stagedItems = stagedItems.map((it) => ({
        ...it,
        properties: { ...(it.properties || {}), ...extra }
      }));
      if (!stageOnlyBundle && mainItem) {
        mainItem.properties = { ...(mainItem.properties || {}), ...extra };
      }
    }

    // 6) Single /cart/add.js request
    const addBody = { items: stageOnlyBundle ? stagedItems : [ mainItem, ...stagedItems ] };
    __spd('atc.POST /cart/add.js', { itemCount: addBody.items.length, body: addBody });
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(addBody)
    });

    if (!res.ok) {
      const errText = await res.text().catch(()=> '');
      __spd('atc.add.FAILED', { httpStatus: res.status, errText });
      try { window.CIKB?.restageLineItems?.(stagedItems); } catch {}
      throw new Error(errText || 'Failed to add items');
    }

    const addedBody = await res.clone().json().catch(() => null);
    __spd('atc.add.response', { httpStatus: res.status, items: __spdKeys(addedBody?.items) });

    /* The staged bundle has just been consumed -- the items moved to the cart. Say so
     * on the document so the builders can put back what the customer still has on
     * screen; otherwise the render loses its charms and a second add is a bare
     * collar. The theme's own product-add:success fires on the section, not here. */
    try {
      document.dispatchEvent(new CustomEvent('sp:staged:consumed', {
        bubbles: true, detail: { bundles: consumedBundles } }));
    } catch (e) {}

    // 7) Hydrate and open drawer (your existing helpers)
    __spd('atc.fetching /cart.js (raw, pre-render snapshot)');
    const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r=>r.json());
    __spd('atc.cart.js response', { item_count: cart.item_count, items: __spdKeys(cart.items) });

    // ── KEY CHECK (debug-only) ───────────────────────────────────────────────
    // At this point, abconvert / other cart transform apps may not have run yet.
    // The line item keys we just received may change once they decorate the lines.
    // Re-snapshot in 800ms to see if any keys mutated. GATED so live traffic
    // isn't doing this extra fetch on every add.
    try {
      const __spdOn = localStorage.getItem('sp_cart_debug') === '1' || /[?&]debugCart=1/i.test(location.search);
      if (__spdOn) {
        setTimeout(async () => {
          try {
            const later = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r=>r.json());
            const before = new Map((cart.items || []).map(i => [i.variant_id, i.key]));
            const drift = (later.items || []).filter(i => before.get(i.variant_id) !== i.key);
            if (drift.length) {
              __spd('atc.KEY_DRIFT_DETECTED', { changedCount: drift.length, drift: drift.map(i => ({ variant: i.variant_id, oldKey: before.get(i.variant_id), newKey: i.key, props: Object.keys(i.properties || {}) })) });
            } else {
              __spd('atc.no key drift', { item_count: later.item_count });
            }
          } catch {}
        }, 800);
      }
    } catch {}
    // ─────────────────────────────────────────────────────────────────────────

    await this.hydrateDrawerSections(cart);
    __spd('atc.drawer hydrated');
    this.openDrawerNow();
    try {
      document.dispatchEvent(new CustomEvent(EVENTS.CART_CHANGED, { detail: { cartData: cart }, bubbles: true }));
    } catch {}
    __spd('atc.complete (CART_CHANGED dispatched)');
  }


  // Replace drawer HTML using the Sections API (adjust section handles/selectors if your theme differs)
  async hydrateDrawerSections(cartData) {
    try {
      const sections = ['cart-drawer', 'cart-icon-bubble'].join(',');
      const url = `${window.location.pathname}?sections=${sections}`;
      const r = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (r.ok) {
        const html = await r.json(); // map: { 'cart-drawer': '<html>', 'cart-icon-bubble': '<html>' }

        // Replace drawer
        const drawerHost = document.querySelector('[data-cart-drawer], [js-cart-drawer], #CartDrawer');
        if (drawerHost && html['cart-drawer']) {
          drawerHost.innerHTML = html['cart-drawer'];
        }

        // Replace cart bubble
        const bubble = document.querySelector('[data-cart-count-bubble], [js-cart-count-bubble]');
        if (bubble && html['cart-icon-bubble']) {
          bubble.outerHTML = html['cart-icon-bubble'];
        }
        return;
      }
    } catch {
      /* fall through to minimal update below */
    }

    // Minimal fallback: update a count if your theme exposes one
    try {
      const countNode = document.querySelector('[data-cart-count], [js-cart-count]');
      if (countNode && cartData && typeof cartData.item_count === 'number') {
        countNode.textContent = String(cartData.item_count);
      }
    } catch {}
  }

  openDrawerNow() {
    // Prefer theme events
    try {
      if (EVENTS?.CART_DRAWER_OPENING) {
        window.dispatchEvent(new CustomEvent(EVENTS.CART_DRAWER_OPENING));
      }
      if (EVENTS?.CART_DRAWER_OPEN) {
        window.dispatchEvent(new CustomEvent(EVENTS.CART_DRAWER_OPEN));
        return;
      }
    } catch {}

    // Fallback: click a toggle the theme already wires up
    const toggler =
      document.querySelector('[js-cart-toggle]') ||
      document.querySelector('[data-cart-drawer-toggle]') ||
      document.querySelector('[data-open-cart]');
    toggler?.click();
  }
}

if (!customElements.get("product-blocks-buy-buttons")) {
  customElements.define("product-block-buy-buttons", ProductBlockBuyButtons);
}
